import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logoutAdmin } from '../../utils/auth';
import { useSidebar } from '../../contexts/SidebarContext';
import { 
  LayoutDashboard, 
  Folder, 
  FolderTree, 
  Tag, 
  MapPin, 
  Building, 
  Clock, 
  Star, 
  Flag, 
  Users, 
  Globe, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  FolderOpen,
  Hash,
  FileText,
  Newspaper,
  Droplet,
  PhoneCall,
  Landmark,
  Vote
} from 'lucide-react';

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebar();

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/categories', label: 'Categories', icon: Folder },
    { path: '/admin/subcategories', label: 'Subcategories', icon: FolderTree },
    { path: '/admin/tags', label: 'Tags', icon: Tag },
    { path: '/admin/cities', label: 'Cities', icon: MapPin },
    { path: '/admin/businesses', label: 'Businesses', icon: Building },
    { path: '/admin/requests', label: 'Requests', icon: Clock },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/reports', label: 'Reports', icon: Flag },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/content-categories', label: 'Content Categories', icon: FolderOpen },
    { path: '/admin/content-tags', label: 'Content Tags', icon: Hash },
    { path: '/admin/blogs', label: 'Blogs', icon: FileText },
    { path: '/admin/news', label: 'News', icon: Newspaper },
    { path: '/admin/blood-donors', label: 'Blood Donors', icon: Droplet },
    { path: '/admin/officers', label: 'Officers Directory', icon: Landmark },
    { path: '/admin/bhavi-sarpanch', label: 'भावी सरपंच (Candidates)', icon: Vote },
    { path: '/admin/phone-logs', label: 'Phone View Logs', icon: PhoneCall },
  ];

  return (
    <aside 
      className={`bg-gray-800 dark:bg-gray-900 text-white transition-all duration-300 fixed left-0 top-0 h-full z-40 flex flex-col ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      data-collapsed={isCollapsed}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
        {!isCollapsed && (
          <Link to="/admin/dashboard" className="flex items-center space-x-2">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden">
              <img 
                src="/digi_fatehabad_logo.png" 
                alt="Digital Fatehabad Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-lg font-bold">Admin Panel</span>
          </Link>
        )}
        {isCollapsed && (
          <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden mx-auto">
            <img 
              src="/digi_fatehabad_logo.png" 
              alt="Digital Fatehabad Logo" 
              className="w-full h-full object-contain"
            />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg hover:bg-gray-700 transition-colors"
          aria-label="Toggle sidebar"
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto py-4 min-h-0">
        <ul className="space-y-1 px-2">
          {navLinks.map((link) => {
            const IconComponent = link.icon;
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                  title={isCollapsed ? link.label : ''}
                >
                  <IconComponent className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{link.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sidebar Footer */}
      <div className="border-t border-gray-700 p-4 space-y-2 flex-shrink-0">
        <Link
          to="/"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors ${
            isCollapsed ? 'justify-center' : ''
          }`}
          title={isCollapsed ? 'View Public Site' : ''}
        >
          <Globe className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Public Site</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-error-600 hover:bg-error-700 transition-colors ${
            isCollapsed ? 'justify-center' : ''
          }`}
          title={isCollapsed ? 'Logout' : ''}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
